import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { ConfirmationResultModal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { RemoveGroupMemberModalProps } from './RemoveGroupMemberModal.types'

type ModalState = 'confirm' | 'success' | 'failure'

export function RemoveGroupMemberModal({
  isOpen,
  onClose,
  groupId,
  memberId,
  memberName,
  groupName,
  onSuccess
}: RemoveGroupMemberModalProps) {
  const { t } = useTranslation()
  const { groupService } = useServices()
  const [modalState, setModalState] = useState<ModalState>('confirm')

  const [{ isPending }, { mutateAsync: removeGroupMember }] = useMutation(groupService.removeGroupMember)

  const handleRemove = async () => {
    try {
      await removeGroupMember({ groupId, memberId })
      setModalState('success')
    } catch {
      setModalState('failure')
    }
  }

  const handleClose = () => {
    if (!isPending) {
      setModalState('confirm')
      onClose()
    }
  }

  const handleSuccessClose = () => {
    setModalState('confirm')
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState('confirm')
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState === 'confirm' ? undefined : modalState}
      title={t('modal.removeGroupMember.title')}
      primaryButtonText={isPending ? t('modal.removeGroupMember.button.removing') : t('button.remove')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleRemove}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      loading={isPending}
      primaryBtnTestId="remove-group-member-submit-button"
      secondaryBtnTestId="remove-group-member-cancel-button"
      success={{
        title: t('modal.removeGroupMember.success.title'),
        description: (
          <Trans
            i18nKey="modal.removeGroupMember.success.description"
            values={{ name: memberName, groupName }}
            components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
          />
        ),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.removeGroupMember.error.title'),
        description: t('modal.removeGroupMember.error.description'),
        button: t('button.close')
      }}
    >
      <Trans
        i18nKey="modal.removeGroupMember.description"
        values={{ name: memberName, groupName }}
        components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
      />
    </ConfirmationResultModal>
  )
}
